import React, { useCallback, useEffect, useState } from 'react';
import { Route, useHistory } from 'react-router-dom';
import { useMounted, useNotifications } from '@fullstory/react-hooks';
import { HOME_ROUTE, generateRoutePath } from '@fullstory/routes';
import { useSelector } from 'react-redux';
import { fullstorySelectors } from '@fullstory/redux';

interface AuthRouteProps {
  path: React.ComponentProps<typeof Route>['path'];
  render: React.ComponentProps<typeof Route>['render'];
  checkAuthorized: () => Promise<boolean>;

  // Optional props for redirecting the user if they are not authorized.

  // If redirectMessage is provided, it will show a notification with the message.
  redirectMessage?: string;
  // If redirectPath is provided, it will redirect to that path. Otherwise user will be redirected to Home.
  redirectPath?: string;
  // Optional search params to pass to the redirect path.
  redirectSearch?: URLSearchParams;
}

// Renders a Route after checking if the user is authorized to access it.
// If the user is not authorized, it will redirect to the redirectPath or Home if no redirectPath is provided.
export const AuthRoute: React.FC<AuthRouteProps> = ({
  path,
  render,
  checkAuthorized,
  redirectMessage,
  redirectPath,
  redirectSearch,
}) => (
  <Route
    data-component="AuthRoute"
    path={path}
    render={props => (
      <InnerAuthRoute
        checkAuthorized={checkAuthorized}
        redirectMessage={redirectMessage}
        redirectPath={redirectPath}
        redirectSearch={redirectSearch}
      >
        {render?.(props)}
      </InnerAuthRoute>
    )}
  />
);

interface InnerAuthRouteProps extends Omit<AuthRouteProps, 'render' | 'path'> {
  children: React.ReactNode;
}

export const InnerAuthRoute: React.FC<InnerAuthRouteProps> = ({
  checkAuthorized,
  redirectMessage,
  redirectPath,
  redirectSearch,
  children,
}) => {
  const notifications = useNotifications();
  const history = useHistory();
  const isMounted = useMounted();
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const [authorized, setAuthorized] = useState(false);

  const redirect = useCallback(() => {
    history.replace({
      pathname: redirectPath || generateRoutePath(HOME_ROUTE, { orgId }),
      search: redirectSearch?.toString() || '',
    });
  }, [history, redirectPath, redirectSearch, orgId]);

  useEffect(() => {
    checkAuthorized()
      .then(authorized => {
        if (isMounted() && !authorized) {
          if (redirectMessage) {
            notifications.info(redirectMessage);
          }
          redirect();
        } else if (isMounted()) {
          setAuthorized(true);
        }
      })
      .catch(() => {
        // if we can't verify if the user is authorized, we should redirect them to the redirect path.
        redirect();
      });
  }, [
    checkAuthorized,
    history,
    notifications,
    redirectMessage,
    orgId,
    isMounted,
    redirectPath,
    redirectSearch,
    redirect,
  ]);

  return <>{authorized && children}</>;
};
